import React, { useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import {
    GotvCampaignFooter,
    DEFAULT_SOCIAL_HEADER,
} from "QuorumGrassroots/widgets/GotvCampaignFooter/GotvCampaignFooter"
import { useGotvSocialSharing } from "QuorumGrassroots/widgets/GotvCampaignFooter/useGotvSocialSharing"
import { CampaignSubpageHeader } from "QuorumGrassroots/widgets/ReusableComponents/CampaignSubpageHeader"
import {
    SubpageContainer,
    LoadingContainer,
    Wrapper,
} from "QuorumGrassroots/pages/campaign/candidates/CandidatesPage.styles"
import { useCampaign, useCandidates } from "QuorumGrassroots/services/grassrootsCampaign"
import { CampaignTitleSubtitle } from "QuorumGrassroots/campaign-forms/components/GotvV2Form/components/CampaignTitleSubtitle/CampaignTitleSubtitle"
import {
    catalogCandidatesByRole,
    buildOfficialCardProps,
    buildCandidateActions,
} from "QuorumGrassroots/pages/campaign/candidates/CandidatesPage.helper"
import { CollapsibleSection } from "QuorumGrassroots/campaign-forms/components/GotvV2Form/components/CollapsibleSection/CollapsibleSection"
import { LoadingSkeleton } from "QuorumGrassroots/campaign-forms/components/GotvV2Form/components/LoadingSkeleton/LoadingSkeleton"
import { OfficialCard } from "QuorumGrassroots/widgets/ReusableComponents/OfficialCard"
import { EmptyState } from "QuorumGrassroots/widgets/ReusableComponents/EmptyState/EmptyState"
import {
    FilterState,
    initializeFilters,
} from "QuorumGrassroots/widgets/ReusableComponents/FilterButton/FilterButton.helper"
import { CandidateDetailsModal } from "QuorumGrassroots/widgets/ReusableComponents/CandidateDetailsModal/CandidateDetailsModal"

const CURRENT_YEAR = new Date().getFullYear().toString()

export const CandidatesPage: React.FC = () => {
    const { id } = useParams<{ id: string }>()

    const [filters, setFilters] = useState<FilterState>(initializeFilters())
    const [candidateDetailsId, setCandidateDetailsId] = useState<string | null>(null)

    const { data: campaign, isLoading: isCampaignLoading, isError: isCampaignError } = useCampaign(id)
    const { data: candidates, isLoading: isCandidatesLoading } = useCandidates(CURRENT_YEAR)

    const selectedCandidate = useMemo(() => {
        if (!candidateDetailsId) return null
        return candidates.find((candidate) => candidate.id === candidateDetailsId) ?? null
    }, [candidateDetailsId, candidates])

    const filteredCandidates = useMemo(() => {
        if (!candidates) return []

        return candidates.filter((candidate) => {
            const isCandidatePartySelected = filters.party[candidate.party.toLowerCase()]
            const isCandidateIncumbentSelected = filters.type.incumbent === candidate.candidate.incumbent
            const isCandidateChallengerSelected = filters.type.challenger === !candidate.candidate.incumbent
            if (!filters.type.incumbent && !filters.type.challenger) return false
            return isCandidatePartySelected && (isCandidateIncumbentSelected || isCandidateChallengerSelected)
        })
    }, [candidates, filters])

    const catalogedCandidates = useMemo(() => {
        if (!filteredCandidates) return []
        return catalogCandidatesByRole(filteredCandidates)
    }, [filteredCandidates])

    const isLoading = isCampaignLoading || isCandidatesLoading

    const socialPlatforms = useGotvSocialSharing({
        socialMediaPlatforms: campaign?.social_media_platforms,
        shareUrl: window.location.href,
        shareText: campaign?.social_share_text ?? `Help ${campaign?.name} get out the vote!`,
        imageUrl: campaign?.social_image_url,
    })

    if (isLoading)
        return (
            <Wrapper>
                <LoadingContainer>
                    <LoadingSkeleton />
                </LoadingContainer>
            </Wrapper>
        )

    if (isCampaignError)
        return (
            <Wrapper>
                <SubpageContainer>
                    <p>Error loading campaign. Please try again later.</p>
                </SubpageContainer>
            </Wrapper>
        )

    return (
        <SubpageContainer>
            <CampaignTitleSubtitle campaign={campaign} />
            <CampaignSubpageHeader
                title="Explore Candidates"
                backUrl={`/campaign/${id}`}
                dataCy="explore-candidates-header"
                withExtraMarginBottom
                showFilterButton
                showTypeFilter
                onFiltersChange={(filter: FilterState) => {
                    setFilters(filter)
                }}
            />

            {catalogedCandidates.length > 0 ? (
                catalogedCandidates.map((role) => (
                    <CollapsibleSection key={role.name} title={role.name}>
                        {role.candidates.map((candidate) => (
                            <OfficialCard
                                key={candidate.id}
                                official={buildOfficialCardProps(candidate)}
                                actions={buildCandidateActions(candidate)}
                                data-plum-id={`candidates.${candidate.id}`}
                                onClick={() => setCandidateDetailsId(candidate.id)}
                            />
                        ))}
                    </CollapsibleSection>
                ))
            ) : (
                <EmptyState
                    icon="fa-users"
                    title="No candidates"
                    description="No candidates were found for this election"
                    dataCy="candidates-empty-state"
                />
            )}

            <GotvCampaignFooter
                socialPlatforms={socialPlatforms}
                socialHeading={campaign.social_header ?? DEFAULT_SOCIAL_HEADER}
            />

            {selectedCandidate && (
                <CandidateDetailsModal
                    candidate={selectedCandidate}
                    isOpen={selectedCandidate !== null}
                    onClose={() => setCandidateDetailsId(null)}
                />
            )}
        </SubpageContainer>
    )
}
